import React, { type ReactNode, useCallback, useMemo, useState } from 'react';
import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
import Box from '@mui/material/Box';
import Checkbox, { type CheckboxProps } from '@mui/material/Checkbox';
import Typography from '@mui/material/Typography';
import { Stack, type SxProps, type Theme } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import {
  type GridColDef,
  type GridInitialState,
  type GridRenderCellParams,
  type GridRowId,
  type GridRowSelectionModel,
  GridToolbarQuickFilter,
} from '@mui/x-data-grid-premium';

import { type Account, accountsSelectors } from 'app-components/flux/accounts';
import { categoriesSelectors } from 'app-components/flux/categories';
import { chartOfAccountsUtils } from 'app-components/flux/chart-of-accounts';
import { transactionsSelectors } from 'app-components/flux/transactions';

import AccountMultiSelect from 'components/SimplifiSelects/AccountMultiSelect';
import ZeroStateView from 'components/ZeroStateView';

import { reduce } from 'app-components/utils/core/immutableHelpers';
import { TypographyCell } from 'components/DataGridCells//TypographyCell';
import { QAmountFieldCell } from 'components/DataGridCells/QAmountFieldCell';
import DataGridStyle1 from 'components/DataGridStyle1';
import { getSplitColumn } from 'components/TransactionItemsGrid/TransactionItemsColumns/splitIconColumn';
import { TextCell } from 'components/TransactionItemsGrid/TransactionItemsColumns/utils';
import { useAppSelector, useAppStore } from 'hooks/redux';
import { DateTime } from 'luxon';
import type { JSX } from 'react/jsx-runtime';
import { type RootStore } from 'store';

const sx = {
  '& .MuiDataGrid-row': {
    borderRadius: '8px',
    '&:hover': {
      cursor: 'pointer',
    },
  },
  '& .MuiDataGrid-row:focus, & .MuiDataGrid-cell:focus': {
    outline: 'none',
  },
  minHeight: 300,
  maxHeight: 600,
  overflow: 'hidden',
  '& .MuiDataGrid-main': {
    marginTop: 0, // prevent space for header
  },
  '& .MuiDataGrid-columnHeaders': {
    display: 'none',
  },
};

const initialState: GridInitialState = {
  sorting: {
    sortModel: [{ field: 'date', sort: 'desc' }],
  },
};

type StandardToolbarProps = {
  selectedAccountIds: string[];
  setSelectedAccountIds: (ids: string[]) => void;
  setSelectedIds: (ids: string[]) => void;
  showAccountFilter?: boolean;
  subtitle?: string;
  ToolbarFooter?: ReactNode;
};

const StandardToolbar = (props: StandardToolbarProps) => {
  return (
    <Stack>
      <Box
        display='flex'
        alignItems='center'
        justifyContent='space-between'
        padding={1.5}
        gap={1}
        pt={0.5}
        width='100%'
      >
        {props.subtitle ? <Typography variant='bodyRegular'>{props.subtitle}</Typography> : null}
        {props.showAccountFilter && (
          <AccountMultiSelect
            showAllItemsMenu
            selectedItems={props.selectedAccountIds}
            onSelected={(myAccounts: Account[]) => {
              props.setSelectedAccountIds(
                (myAccounts ?? []).reduce<string[]>((acc, account) => {
                  if (account.id !== null && account.id !== undefined) {
                    acc.push(account.id);
                  }
                  return acc;
                }, []),
              );
              props.setSelectedIds([]);
            }}
            accountsSelector={accountsSelectors.getAccountsById}
          />
        )}
        <GridToolbarQuickFilter
          variant='outlined'
          size='small'
          placeholder='Search transactions'
          sx={{
            padding: 0,
            flexGrow: 1,
            minWidth: 0,
          }}
          debounceMs={350}
        />
      </Box>
      {props.ToolbarFooter}
    </Stack>
  );
};

const ZeroStateGrid = () => {
  return <ZeroStateView qIllustration='accounts' description="We couldn't find any transactions" />;
};

const CheckboxOverride = (props: JSX.IntrinsicAttributes & CheckboxProps) => {
  return <Checkbox {...props} checkedIcon={<CheckRoundedIcon sx={{ color: 'primary.main' }} />} icon={<div />} />;
};

const getDefaultAccountIds = (store: RootStore) => {
  const accountsById = accountsSelectors.getAccountsById(store.getState());
  return accountsById.reduce((acc: string[], account: Account) => {
    if (account.id !== null && account.id !== undefined) {
      acc.push(account.id);
    }
    return acc;
  }, []);
};

export default function TransactionItemSelect(props: {
  selectedIds: any;
  setSelectedIds: any;
  checkboxSelection?: boolean;
  disableMultipleRowSelection?: boolean;
  showAccountFilter?: boolean;
  txnItemIds: any;
  toolbar?: any;
  subtitle?: string;
  overrideSx?: SxProps<Theme>;
  ToolbarFooter?: ReactNode;
  initialAccountIds?: Array<string>;
}) {
  const {
    selectedIds,
    setSelectedIds,
    checkboxSelection = false,
    disableMultipleRowSelection = true,
    txnItemIds,
    showAccountFilter = true,
    toolbar,
    subtitle,
    overrideSx,
    ToolbarFooter,
    initialAccountIds,
  } = props;

  const theme = useTheme();
  const store = useAppStore();

  const transactionItems = useAppSelector((state) =>
    transactionsSelectors.getTransactionItemsByIdOfTypes(state, transactionsSelectors.txnTypesCash),
  );
  const accountsById = useAppSelector(accountsSelectors.getAccountsById);
  const categoriesById = useAppSelector(categoriesSelectors.getCategoriesById);

  const [selectedAccountIds, setSelectedAccountIds] = useState<string[]>(
    () => initialAccountIds || getDefaultAccountIds(store),
  );

  const columns: GridColDef[] = useMemo(() => {
    const isSelected = (id?: GridRowId) => selectedIds.includes(id);
    return [
      {
        field: 'date',
        type: 'date',
        headerName: 'date',
        width: 125,
        renderCell: (params: GridRenderCellParams) => (
          <TypographyCell params={params} sx={isSelected(params.id) ? { color: 'primary.main' } : {}}>
            {DateTime.fromJSDate(params.value).toFormat('LLL dd, yyyy')}
          </TypographyCell>
        ),
      },
      {
        field: 'payee',
        headerName: 'payee',
        flex: 1,
        renderCell: (params: GridRenderCellParams) => (
          <TextCell sx={isSelected(params.id) ? { color: 'primary.main' } : {}} value={params.value} />
        ),
      },
      getSplitColumn(),
      {
        field: 'category',
        headerName: 'Category',
        flex: 1,
        renderCell: (params: GridRenderCellParams) => (
          <TextCell
            sx={isSelected(params.id) ? { color: 'primary.main' } : {}}
            value={params.formattedValue.split(':').pop()}
          />
        ),
      },
      {
        field: 'amount',
        headerName: 'Amount',
        width: 160,
        align: 'right',
        renderCell: (params: GridRenderCellParams) => (
          <QAmountFieldCell
            params={params}
            sx={isSelected(params.id) ? { color: `${theme.palette.primary.main} !important` } : {}}
            showColor='positive'
            expectedSign='negative'
          />
        ),
      },
    ];
  }, [selectedIds, theme]);

  const rows = useMemo(() => {
    type TransactionRow = {
      id: string;
      date: Date;
      payee: string;
      split: boolean;
      category: string;
      amount: number;
    };

    return reduce<string, TransactionRow[]>(txnItemIds, [], (acc, id) => {
      const item = transactionItems.get(id);

      if (
        !item ||
        !item.item ||
        (selectedAccountIds.length > 0 && !selectedAccountIds.includes(item.item.accountId ?? ''))
      ) {
        return acc;
      }
      acc.push({
        id,
        date: DateTime.fromISO(item.item.postedOn ?? '').toJSDate(),
        payee: item.item.payee ?? 'N/A',
        split: Boolean(item.item.split),
        category: chartOfAccountsUtils.getCoaString(item.item.coa, categoriesById, accountsById, false),
        amount: item.item.amount ?? 0,
      });

      return acc;
    });
  }, [transactionItems, txnItemIds, categoriesById, accountsById, selectedAccountIds]);

  const handleSelect = useCallback(
    (newRowSelectionModel: GridRowSelectionModel) => {
      setSelectedIds(newRowSelectionModel);
    },
    [setSelectedIds],
  );

  const slots = useMemo(
    () => ({
      baseCheckbox: disableMultipleRowSelection ? CheckboxOverride : undefined,
      toolbar: () =>
        toolbar ?? (
          <StandardToolbar
            selectedAccountIds={selectedAccountIds}
            setSelectedAccountIds={setSelectedAccountIds}
            showAccountFilter={showAccountFilter}
            setSelectedIds={setSelectedIds}
            subtitle={subtitle}
            ToolbarFooter={ToolbarFooter}
          />
        ),
      noRowsOverlay: () => <ZeroStateGrid />,
    }),
    [
      disableMultipleRowSelection,
      toolbar,
      selectedAccountIds,
      showAccountFilter,
      setSelectedIds,
      subtitle,
      ToolbarFooter,
    ],
  );

  return (
    <DataGridStyle1
      ignoreDiacritics
      hideFooter
      checkboxSelection={checkboxSelection}
      disableMultipleRowSelection={disableMultipleRowSelection}
      columns={columns}
      rows={rows}
      onRowSelectionModelChange={handleSelect}
      rowSelectionModel={selectedIds}
      columnHeaderHeight={0}
      slots={slots}
      sx={{ ...sx, ...overrideSx }}
      initialState={initialState}
    />
  );
}
